/**
 * Turning a form's JSON Schema into a form a person can read.
 *
 * The canonical vocabulary is written for machines: FILER_FEDERAL_ID_NUMBER,
 * BOX1A_NONEMPLOYEE_COMPENSATION. The schema is what says which fields exist,
 * in what order and of what type, so the drawer reads it rather than carrying a
 * table of its own that would go stale the moment a form changes.
 */

/** What a field is, which decides how it is rendered and how it is read back. */
export type FieldKind = 'amount' | 'indicator' | 'tin' | 'text';

export const FIELD_GROUPS = ['Filer', 'Recipient', 'Amounts', 'State', 'Other'] as const;
export type FieldGroup = (typeof FIELD_GROUPS)[number];

export interface RecordField {
  name: string;
  label: string;
  kind: FieldKind;
  group: FieldGroup;
  /** Set by the e-file, not editable here. */
  pinned?: boolean;
  required: boolean;
}

export interface FieldGroupView {
  group: FieldGroup;
  fields: RecordField[];
}

/** The shape of the draft 7 document GET /v1/forms/{formType}/schema returns. */
export interface FormJsonSchema {
  properties?: Record<string, { type?: string | string[]; description?: string }>;
  required?: string[];
}

/** Written as initialisms in the canonical vocabulary and read as initialisms. */
const INITIALISMS = new Set(['ID', 'TIN', 'EIN', 'SSN', 'ZIP', 'IRS', 'US', 'FATCA', 'CFSF', 'LLC']);

/** Abbreviations worth spelling out, because the label is read once and acted on. */
const SPELLED_OUT: Record<string, string> = {
  IND: 'indicator',
  AMT: 'amount',
  NUM: 'number',
  CD: 'code',
  ADDR: 'address',
};

function word(token: string): string {
  if (INITIALISMS.has(token)) return token;
  const spelled = SPELLED_OUT[token];
  if (spelled) return spelled;
  // FILER_NAME1 reads as "Filer name 1", not "Filer name1".
  const trailing = /^([A-Za-z]+)(\d+)$/.exec(token);
  if (trailing) return `${trailing[1]?.toLowerCase()} ${trailing[2]}`;
  return token.toLowerCase();
}

function sentence(tokens: string[]): string {
  const text = tokens.map(word).join(' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * BOX1A_NONEMPLOYEE_COMPENSATION reads as "Box 1a Nonemployee compensation":
 * the box number stays a label of its own, and what follows is a sentence.
 */
/** IRS element-name suffixes, spelled out the way a person reads the box. */
const IRIS_SUFFIXES: Record<string, string> = {
  Nm: 'name',
  Txt: 'text',
  Amt: 'amount',
  Cd: 'code',
  Ind: 'indicator',
  Num: 'number',
  Dt: 'date',
  Pct: 'percent',
  Grp: 'group',
  Desc: 'description',
  Id: 'id',
  Qty: 'quantity',
  Yr: 'year',
};

/** True for a key that came from the customer's own IRIS XML rather than the canonical vocabulary. */
export function isIrisElementPath(name: string): boolean {
  return /^[A-Z][A-Za-z0-9]*(\.[A-Z][A-Za-z0-9]*)*(\.\d+)?$/.test(name) && /[a-z]/.test(name);
}

function irisWords(element: string): string {
  const parts = element.match(/[A-Z]+(?![a-z])|[A-Z][a-z0-9]*|\d+/g) ?? [element];
  return parts
    .map((part, index) => {
      if (index === parts.length - 1 && IRIS_SUFFIXES[part]) return IRIS_SUFFIXES[part];
      if (INITIALISMS.has(part.toUpperCase()) && part === part.toUpperCase()) return part;
      return part.toLowerCase();
    })
    .join(' ');
}

/**
 * IRIS element names that the paper form already has a caption for.
 *
 * `PersonFirstNm` read as "Person first name (person name)" by the general rule, which is a
 * description of the XML rather than of the box: the form calls it "First name", and a
 * customer checking a record against the copy they sent their recipient is reading the form.
 */
const IRIS_CAPTIONS: Record<string, string> = {
  PersonFirstNm: 'First name',
  PersonMiddleNm: 'Middle name',
  PersonLastNm: 'Last name',
  SuffixNm: 'Suffix',
  PersonNm: 'Name',
  BusinessNameLine1Txt: 'Business name',
  BusinessNameLine2Txt: 'Business name, line 2',
  PersonNameControlTxt: 'Name control',
  BusinessNameControlTxt: 'Name control',
  TINTypeCd: 'TIN type',
  TINSubmittedTypeCd: 'TIN type',
  AddressLine1Txt: 'Street address',
  AddressLine2Txt: 'Street address, line 2',
  CityNm: 'City or town',
  StateAbbreviationCd: 'State',
  ZIPCd: 'ZIP code',
  CountryCd: 'Country',
  ProvinceOrStateNm: 'Province or state',
  ForeignPostalCd: 'Foreign postal code',
  PhoneNum: 'Telephone number',
  ForeignPhoneNum: 'Telephone number',
  EmailAddressTxt: 'Email address',
  RecipientAccountNum: 'Account number',
  AccountNum: 'Account number',
  PayerStateIdNum: "Payer's state number",
  StateAbbreviationTxt: 'State',
  StateIncomeTaxWithheldAmt: 'State tax withheld',
  StateIncomeAmt: 'State income',
  LocalIncomeTaxWithheldAmt: 'Local tax withheld',
  LocalIncomeAmt: 'Local income',
  TaxYr: 'Tax year',
  CorrectedInd: 'Corrected',
  SecondTINNoticeInd: '2nd TIN notice',
  FATCAFilingRequirementInd: 'FATCA filing requirement',
};

/**
 * `RecipientDetail.PersonFirstNm` reads as "First name".
 *
 * The wrapper is added only where it separates two boxes that would otherwise carry the same
 * caption - a foreign address beside a US one - and a repeated element keeps its number,
 * because two state lines both called "State tax withheld" cannot be told apart.
 */
function labelForIrisPath(path: string): string {
  const all = path.split('.');
  const repeat = /^\d+$/.test(all[all.length - 1] ?? '') ? all[all.length - 1] : null;
  const segments = all.filter((segment) => !/^\d+$/.test(segment));
  const leaf = segments[segments.length - 1] ?? path;
  const text = irisWords(leaf);
  const named = IRIS_CAPTIONS[leaf] ?? text.charAt(0).toUpperCase() + text.slice(1);
  const parent = segments.length > 1 ? segments[segments.length - 2] : null;
  const context = parent && /Foreign/.test(parent) && !/Foreign/.test(leaf) ? 'foreign address' : null;
  const label = context ? `${named} (${context})` : named;
  return repeat ? `${label} (${repeat})` : label;
}

/** Keys Tab Service itself adds to a record filed from XML. */
const SUMMARY_LABELS: Record<string, string> = {
  RECIPIENT_NAME: 'Recipient name',
  tinLast4: 'Taxpayer ID (last 4)',
  irsRecordId: 'IRS record id',
  submissionId: 'IRS submission id',
};

export function labelForField(name: string): string {
  if (name === 'recordRef') return 'Record reference';
  if (SUMMARY_LABELS[name]) return SUMMARY_LABELS[name];
  if (isIrisElementPath(name)) return labelForIrisPath(name);
  const tokens = name.split('_').filter(Boolean);
  const first = tokens[0] ?? name;
  const box = /^BOX(\d+)([A-Z]*)$/.exec(first);
  if (box) {
    const rest = tokens.slice(1);
    const suffix = (box[2] ?? '').toLowerCase();
    const head = `Box ${box[1]}${suffix}`;
    return rest.length > 0 ? `${head} ${sentence(rest)}` : head;
  }
  return sentence(tokens);
}

/**
 * What the record is, as opposed to what it says. The form and the year come from the e-file
 * the record was filed in, so they are shown but never offered for editing: changing one here
 * would leave a record claiming a form its own e-file is not for. Tab Service refuses the
 * change on the server as well.
 */
const PINNED_FIELDS = new Set(['FORM_TYPE', 'TAX_YEAR', '1099_YEAR']);

export function isPinnedField(name: string): boolean {
  return PINNED_FIELDS.has(name);
}

export function groupForField(name: string): FieldGroup {
  if (isIrisElementPath(name)) {
    if (/Recipient|Payee|Employee|Borrower|Student/.test(name)) return 'Recipient';
    if (/Issuer|Filer|Payer|Lender|Transmitter/.test(name)) return 'Filer';
    if (/State|Local/.test(name)) return 'State';
    if (/Amt$|Amt\.\d+$|Pct$/.test(name)) return 'Amounts';
    return 'Other';
  }
  if (name === 'RECIPIENT_NAME' || name === 'tinLast4') return 'Recipient';
  if (/^(FILER|PAYER|ISSUER|TRANSMITTER)_/.test(name)) return 'Filer';
  if (/^(RECIPIENT|EMPLOYEE|PAYEE)_/.test(name)) return 'Recipient';
  if (/^BOX/.test(name)) return 'Amounts';
  if (/STATE/.test(name)) return 'State';
  return 'Other';
}

export function kindForField(name: string, type?: string | string[]): FieldKind {
  const types = Array.isArray(type) ? type : type ? [type] : [];
  if (isIrisElementPath(name)) {
    if (/(TIN|SSN|EIN)$/.test(name) || name === 'tinLast4') return 'tin';
    if (/Ind$/.test(name)) return 'indicator';
    if (/Amt$/.test(name)) return 'amount';
    return 'text';
  }
  if (name === 'tinLast4') return 'tin';
  if (types.includes('boolean') || /_(IND|INDICATOR)$/.test(name)) return 'indicator';
  if (/(_ID_NUMBER|_TIN$|^TIN_|_TIN_|FEDERAL_ID_NUMBER|_SSN$|_EIN$)/.test(name)) return 'tin';
  if (types.includes('number') || types.includes('integer')) return 'amount';
  return 'text';
}

/**
 * Every field to render, in schema order, with anything the record carries that
 * the schema does not mention added at the end rather than dropped. `recordRef`
 * is left out: it identifies the record and is shown in the header.
 */
export function fieldsFromSchema(
  schema: FormJsonSchema | undefined,
  canonical: Record<string, unknown> | undefined,
): RecordField[] {
  const required = new Set(schema?.required ?? []);
  const names: string[] = [];
  // A record filed from the customer's own IRIS XML is not written in the canonical
  // vocabulary, so the form's schema does not describe it: adding its properties would draw a
  // hundred empty boxes beside the ones the record actually carries.
  const filedAsXml = Object.keys(canonical ?? {}).some((name) => isIrisElementPath(name));
  for (const name of Object.keys(schema?.properties ?? {})) {
    if (name !== 'recordRef' && !filedAsXml) names.push(name);
  }
  for (const name of Object.keys(canonical ?? {})) {
    if (name !== 'recordRef' && !names.includes(name)) names.push(name);
  }

  return names.map((name) => ({
    name,
    label: labelForField(name),
    kind: kindForField(name, schema?.properties?.[name]?.type),
    group: groupForField(name),
    required: required.has(name),
    pinned: isPinnedField(name),
  }));
}

/** The same fields under their headings, in the order the groups are read. */
export function groupFields(fields: RecordField[]): FieldGroupView[] {
  return FIELD_GROUPS.map((group) => ({ group, fields: fields.filter((field) => field.group === group) })).filter(
    (entry) => entry.fields.length > 0,
  );
}

/** True for a value Tab Service masked before sending it, such as ***-**-1234. */
export function isMaskedValue(value: unknown): boolean {
  return typeof value === 'string' && value.includes('*');
}

/** Amounts are held as text while they are edited and shown to two decimals. */
export function formatAmount(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed.toFixed(2) : String(value);
}

/**
 * Whether an indicator is ticked, however the record spells it.
 *
 * The file intake stores a ticked box as "X"; Pub 1220 files carry "1"; a JSON client may
 * send true. All of them are the box being ticked, and anything else is not.
 */
export function isIndicatorOn(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value !== 'string') return false;
  return /^(X|1|Y|YES|TRUE)$/i.test(value.trim());
}

/**
 * What goes back on the wire for one field, given what the input holds.
 *
 * An indicator goes as "X" or blank, which is how the file intake stores it and what the
 * server's checks accept. A boolean was refused as "Invalid input".
 */
export function valueForWire(kind: FieldKind, value: string | boolean): unknown {
  if (kind === 'indicator') return isIndicatorOn(value) ? 'X' : '';
  if (kind !== 'amount') return value;
  if (value === '' || typeof value !== 'string') return value === '' ? '' : value;
  const parsed = Number(value.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : value;
}

/** How a value reads in the form. Amounts to two decimals, everything as text. */
export function valueForInput(kind: FieldKind, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (kind === 'amount') return formatAmount(value);
  return String(value);
}

/** The value shown under a field as "Filed value", when it differs. */
export function filedValueLabel(kind: FieldKind, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'empty';
  if (kind === 'indicator') return isIndicatorOn(value) ? 'Yes' : 'No';
  return valueForInput(kind, value);
}
